import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Upload, FileText, Download, History, CheckCircle2, XCircle, Send, Trash2, Eye, Layers } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  submitted: { label: "Submitted", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const DISCIPLINES = ["Architectural","Structural","Electrical","Plumbing","HVAC","Interior","Landscape","Other"];
const CATEGORIES = ["Concept","Presentation","Working","Shop","Tender","GFC","As-Built","Revision","Other"];

const fmt = (n) => { const kb = n/1024; return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb/1024).toFixed(2)} MB`; };

export default function DrawingsTab({ pid, user, backendUrl }) {
  const [drawings, setDrawings] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState({ title: "", drawing_number: "", discipline: "Architectural", category: "Working", description: "", change_note: "Initial upload" });
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState("approved");
  const [reviewNote, setReviewNote] = useState("");
  const fileRef = useRef(null);
  const revFileRef = useRef(null);

  const canApprove = ["super_admin","director","architect","hr"].includes(user?.role);
  const canEdit = ["super_admin","director","architect","interior_designer","site_engineer","hr","purchase_manager"].includes(user?.role);

  const load = () => api.get(`/projects/${pid}/drawings`).then((r) => setDrawings(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const uploadNew = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a file");
    if (!form.title.trim()) return toast.error("Title required");
    const fd = new FormData();
    fd.append("file", file);
    ["title","drawing_number","discipline","category","description","change_note"].forEach(k => fd.append(k, form[k]));
    setUploading(true);
    try {
      await api.post(`/projects/${pid}/drawings`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Drawing uploaded");
      setUploadOpen(false); setForm({ title: "", drawing_number: "", discipline: "Architectural", category: "Working", description: "", change_note: "Initial upload" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const uploadRevision = async (drawing) => {
    const file = revFileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a file");
    const note = window.prompt("Change note for this revision:", `Revision ${(drawing.current_version||1)+1}`);
    if (note === null) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("change_note", note);
    try {
      await api.post(`/projects/${pid}/drawings/${drawing.id}/revisions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("New revision uploaded");
      if (revFileRef.current) revFileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submit = async (d) => {
    try { await api.patch(`/projects/${pid}/drawings/${d.id}/status`, { status: "submitted" }); toast.success("Submitted for approval"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openReview = (d, action) => { setSelected(d); setReviewAction(action); setReviewNote(""); setReviewOpen(true); };
  const doReview = async () => {
    try {
      await api.patch(`/projects/${pid}/drawings/${selected.id}/status`, { status: reviewAction, note: reviewNote });
      toast.success(`Drawing ${reviewAction}`);
      setReviewOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}" and all its revisions?`)) return;
    try { await api.delete(`/projects/${pid}/drawings/${d.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const revisionUrl = (drawing, rev) => `${backendUrl}/api/uploads/projects/${pid}/drawings/${drawing.id}/${rev.file_name}`;

  return (
    <div className="space-y-4" data-testid="drawings-tab">
      <div className="rounded-2xl bg-card border border-border/60 p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold">Drawings</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Multi-format uploads · version control · approval workflow. {drawings.length} drawing{drawings.length!==1 && "s"} in this project.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setUploadOpen(true)} data-testid="upload-drawing-btn" className="bg-primary text-primary-foreground"><Upload className="w-4 h-4 mr-1.5" /> Upload Drawing</Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {drawings.map(d => {
          const meta = STATUS_META[d.status] || STATUS_META.draft;
          const latest = d.latest;
          return (
            <div key={d.id} data-testid={`drawing-${d.id}`} className="rounded-2xl bg-card border border-border/60 hover:border-primary/40 transition-colors overflow-hidden">
              <div className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{d.drawing_number || d.discipline}{d.category ? ` · ${d.category}` : ""}</div>
                    <h4 className="font-display text-base font-semibold truncate">{d.title}</h4>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                </div>
                {d.description && <p className="text-xs text-muted-foreground line-clamp-2">{d.description}</p>}
                {latest && (
                  <div className="rounded-lg bg-muted/40 border border-border/60 p-3 text-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 font-medium"><Layers className="w-3.5 h-3.5" /> v{latest.version} · {latest.original_name}</div>
                      <div className="text-muted-foreground">{fmt(latest.size||0)}</div>
                    </div>
                    <div className="text-muted-foreground">Uploaded by {latest.uploaded_by_name} · {new Date(latest.created_at).toLocaleDateString()}</div>
                    {latest.change_note && <div className="italic text-muted-foreground truncate">"{latest.change_note}"</div>}
                  </div>
                )}
                {d.revisions?.length > 1 && (
                  <div className="text-[11px] flex items-center gap-1.5 text-muted-foreground"><History className="w-3 h-3" /> {d.revisions.length} revisions</div>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  {latest && (
                    <>
                      <a href={revisionUrl(d, latest)} target="_blank" rel="noreferrer" data-testid={`preview-drawing-${d.id}`}><Button variant="outline" size="sm"><Eye className="w-3.5 h-3.5 mr-1" /> Preview</Button></a>
                      <a href={revisionUrl(d, latest)} download data-testid={`download-drawing-${d.id}`}><Button variant="outline" size="sm"><Download className="w-3.5 h-3.5 mr-1" /> Download</Button></a>
                    </>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setSelected(d)} data-testid={`history-drawing-${d.id}`}><History className="w-3.5 h-3.5 mr-1" /> History</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canEdit && d.status === "draft" && <Button size="sm" onClick={() => submit(d)} data-testid={`submit-drawing-${d.id}`} className="bg-primary text-primary-foreground"><Send className="w-3.5 h-3.5 mr-1" /> Submit for approval</Button>}
                  {canApprove && d.status === "submitted" && (
                    <>
                      <Button size="sm" onClick={() => openReview(d, "approved")} data-testid={`approve-drawing-${d.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                      <Button size="sm" onClick={() => openReview(d, "rejected")} data-testid={`reject-drawing-${d.id}`} variant="outline" className="text-rose-600"><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                    </>
                  )}
                  {canEdit && (
                    <>
                      <label className="inline-block">
                        <input ref={revFileRef} type="file" accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.gif,.webp" className="hidden" onChange={() => uploadRevision(d)} />
                        <Button size="sm" variant="outline" onClick={() => revFileRef.current?.click()} data-testid={`revise-drawing-${d.id}`}><Upload className="w-3.5 h-3.5 mr-1" /> New rev</Button>
                      </label>
                      <button onClick={() => remove(d)} data-testid={`delete-drawing-${d.id}`} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                </div>
                {d.status === "rejected" && d.review_note && (
                  <div className="text-xs text-rose-600 border border-rose-200 bg-rose-50 rounded-md p-2">Rejected note: {d.review_note} — by {d.reviewed_by_name}</div>
                )}
                {d.status === "approved" && d.reviewed_by_name && (
                  <div className="text-xs text-emerald-700">Approved by {d.reviewed_by_name} on {new Date(d.reviewed_at).toLocaleDateString()}</div>
                )}
              </div>
            </div>
          );
        })}
        {!drawings.length && <div className="col-span-full p-10 text-center rounded-2xl border border-dashed border-border/60 text-sm text-muted-foreground"><FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />No drawings uploaded yet.</div>}
      </div>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Drawing</DialogTitle><DialogDescription>Supported: PDF, DWG, DXF, JPG, PNG · max 25 MB</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="col-span-2" data-testid="drawing-title-input" />
              <Input placeholder="Drawing No" value={form.drawing_number} onChange={(e) => setForm({ ...form, drawing_number: e.target.value })} />
              <Select value={form.discipline} onValueChange={(v) => setForm({ ...form, discipline: v })}>
                <SelectTrigger data-testid="drawing-discipline-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DISCIPLINES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="drawing-category-select" className="col-span-2"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Input ref={fileRef} type="file" accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.gif,.webp" data-testid="drawing-file-input" className="file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-primary file:text-primary-foreground hover:file:bg-primary/90" />
            <Button onClick={uploadNew} disabled={uploading} data-testid="submit-upload-btn" className="bg-primary text-primary-foreground w-full">{uploading ? "Uploading…" : "Upload"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{reviewAction === "approved" ? "Approve" : "Reject"} — {selected?.title}</DialogTitle><DialogDescription>Add a review note (visible in the audit log).</DialogDescription></DialogHeader>
          <Textarea placeholder="Comment for team" rows={3} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} data-testid="review-note-input" />
          <Button onClick={doReview} data-testid="confirm-review-btn" className={`${reviewAction === "approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"} text-white`}>{reviewAction === "approved" ? "Approve Drawing" : "Reject Drawing"}</Button>
        </DialogContent>
      </Dialog>

      {/* History dialog (uses selected but not reviewOpen) */}
      <Dialog open={!!selected && !reviewOpen} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{selected?.title} — Revision History</DialogTitle><DialogDescription>Every version is preserved with change note and uploader.</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {selected?.revisions?.map(rev => (
              <div key={rev.id} className="rounded-lg border border-border/60 p-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">v{rev.version} · {rev.original_name}</div>
                  <a href={revisionUrl(selected, rev)} target="_blank" rel="noreferrer" className="text-primary text-xs flex items-center gap-1 hover:underline"><Download className="w-3 h-3" /> Download</a>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">By {rev.uploaded_by_name} · {new Date(rev.created_at).toLocaleString()} · {fmt(rev.size||0)}</div>
                {rev.change_note && <div className="text-xs mt-1 italic">"{rev.change_note}"</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
